import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Space, message, Popconfirm, Row, Col, Card } from "antd";
import { ArrowLeftOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import pushTemplateApi from "../../../api/push-template-api";
import PushRichOptions from "./push-rich-options";
import PushLivePreview from "./push-live-preview";
import { buildRichOptions, contentToFields } from "./push-form-helpers";

const { TextArea } = Input;

function PushTemplateManager() {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [saving, setSaving] = useState(false);

    const watched = Form.useWatch([], form) || {};

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const { data } = await pushTemplateApi.listTemplates();
            setTemplates(data.data.items || []);
        } catch {
            message.error("Không thể tải danh sách mẫu");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchTemplates(); }, []);

    const openCreate = () => { setEditingId(null); form.resetFields(); setOpen(true); };
    const openEdit = (record) => {
        setEditingId(record.id);
        form.setFieldsValue({ name: record.name, ...contentToFields(record) });
        setOpen(true);
    };

    const handleSubmit = async () => {
        let v;
        try { v = await form.validateFields(); } catch { return; }
        const payload = { name: v.name, title: v.title, message: v.message, openPath: v.path || undefined, openUrl: v.url || undefined, ...buildRichOptions(v) };
        try {
            setSaving(true);
            if (editingId) await pushTemplateApi.updateTemplate(editingId, payload);
            else await pushTemplateApi.createTemplate(payload);
            message.success(editingId ? "Đã cập nhật mẫu" : "Đã tạo mẫu");
            setOpen(false);
            fetchTemplates();
        } catch (err) {
            message.error(err?.response?.data?.message || "Lưu mẫu thất bại");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await pushTemplateApi.deleteTemplate(id);
            message.success("Đã xóa mẫu");
            fetchTemplates();
        } catch (err) {
            message.error(err?.response?.data?.message || "Xóa thất bại");
        }
    };

    const columns = [
        { title: "Tên mẫu", dataIndex: "name", width: 200, render: (t) => <strong>{t}</strong> },
        { title: "Tiêu đề", dataIndex: "title", ellipsis: true },
        { title: "Nội dung", dataIndex: "message", ellipsis: true },
        { title: "Cập nhật", dataIndex: "updatedAt", width: 140, render: (ts) => (ts ? dayjs(Number(ts)).format("DD/MM/YY HH:mm") : "-") },
        {
            title: "",
            width: 130,
            render: (_, record) => (
                <Space size="small">
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>Sửa</Button>
                    <Popconfirm title="Xóa mẫu này?" onConfirm={() => handleDelete(record.id)} okText="Xóa" cancelText="Thôi">
                        <Button size="small" danger type="link" icon={<DeleteOutlined />}>Xóa</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="flex-1 p-6 overflow-auto" style={{ background: "rgb(var(--surface-sunken))" }}>
            <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
                <Space>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/push")}>Quay lại</Button>
                    <h1 className="text-xl font-semibold m-0">Mẫu Push Notification</h1>
                </Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Tạo mẫu</Button>
            </div>

            <Card bodyStyle={{ padding: 0 }} style={{ borderRadius: 12, overflow: "hidden" }}>
                <Table rowKey="id" dataSource={templates} columns={columns} loading={loading} pagination={{ pageSize: 20 }} scroll={{ x: "max-content" }} />
            </Card>

            <Modal title={editingId ? "Sửa mẫu" : "Tạo mẫu"} open={open} onOk={handleSubmit} confirmLoading={saving} onCancel={() => setOpen(false)} okText="Lưu" cancelText="Hủy" width={820}>
                <Form form={form} layout="vertical">
                    <Row gutter={24}>
                        <Col xs={24} lg={14}>
                            <Form.Item name="name" label="Tên mẫu" rules={[{ required: true, message: "Nhập tên mẫu" }]}>
                                <Input placeholder="vd: Khuyến mãi cuối tuần" />
                            </Form.Item>
                            <Form.Item name="title" label="Tiêu đề" rules={[{ required: true, max: 120, message: "Tối đa 120 ký tự" }]}>
                                <Input maxLength={120} showCount />
                            </Form.Item>
                            <Form.Item name="message" label="Nội dung" rules={[{ required: true, message: "Nhập nội dung" }]}>
                                <TextArea rows={3} />
                            </Form.Item>
                            <Form.Item name="path" label="Custom path (app)"><Input placeholder="/vouchers" /></Form.Item>
                            <Form.Item name="url" label="URL (web)"><Input placeholder="https://..." /></Form.Item>
                            <PushRichOptions />
                        </Col>
                        <Col xs={24} lg={10}>
                            <div style={{ position: "sticky", top: 0 }}><PushLivePreview values={watched} /></div>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
    );
}

export default PushTemplateManager;
